import { useEffect, useRef } from "react";
import { createRoot } from 'react-dom/client';

/**
 * useInfoWindow - contains infoWindowRef and rootRef, provides functions to open, close, and update info window. Currently used by POI Map
 * 
 * @param {React.RefObject<HTMLDivElement>} mapRef - ref for the map instance in MapComponent
 * 
 * @returns {Function} closeInfoWindow  
 * @returns {Function} openInfoWindow
 * @returns {Function} updateInfoWindowContent
 * 
 * @category Hooks - Google Maps
 */
function useInfoWindow(mapRef) {
    const infoWindowRef = useRef(null);
    const rootRef = useRef(null);

    const openInfoWindow = (contentComponent, position) => {
        if (!position || !mapRef.current) return;

        // Close any previously open InfoWindow
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }

        // Create new InfoWindow instance
        infoWindowRef.current = new window.google.maps.InfoWindow({ pixelOffset: new window.google.maps.Size(0, -80) });

        // Create a container for the content inside the InfoWindow
        const contentContainer = document.createElement('div');
        contentContainer.style.minWidth = '320px';
        contentContainer.style.maxWidth = '400px';
        contentContainer.style.minHeight = '250px';
        contentContainer.style.maxHeight = '1300px';

        // Use createRoot to render contentComponent inside the InfoWindow
        rootRef.current = createRoot(contentContainer);
        rootRef.current.render(contentComponent);

        // Set the content and position of the InfoWindow
        infoWindowRef.current.setContent(contentContainer);
        infoWindowRef.current.setPosition(position);
        infoWindowRef.current.open(mapRef.current);
    };

    // Update the content of the InfoWindow without unmounting
    const updateInfoWindowContent = (newContentComponent) => {

        //there are some cases where the ref is null, but there is a property nested inside of 'current' called '_internalRoot' which is set to null - so it is good to check for this too
        //_internalRoot was causing an issue when hasUnsavedChanges was updated(triggering a useEffect in usePoiMap) when the info window was recently closed - 'current' was set to '_internalRoot: null' instead of simply 'null', so checking (!rootRef.current) was missing some cases of the ref being null
        
        if(rootRef.current === null || rootRef.current._internalRoot === null) return
        
        rootRef.current.render(newContentComponent);  // Re-render the updated content
    };

    const closeInfoWindow = () => {
        if (rootRef.current) {
            rootRef.current.unmount();
        }
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }
    };

    // Cleanup when the component using the hook unmounts
    useEffect(() => {
        return () => {
            closeInfoWindow();  // Ensure cleanup when the component unmounts
        };
    }, []);

    return {
        closeInfoWindow,
        openInfoWindow,
        updateInfoWindowContent
    };
}

export default useInfoWindow;